// Store global. Porte fiel do synapse-os/src/store/useStore.js para TS,
// preservando: history (60 snapshots), undo/redo, universe stack com cam state,
// patchNode (live, sem snapshot) vs updateNode (snap), duplicateNode, lock toggle.

import { create } from 'zustand';
import { tickPhysics, getVisibleNodes, type GraphLink, type GraphNode } from '@synapse/engine';
import { CATEGORY_MAP, NODE_DEFAULTS, uid } from '../utils/constants.js';
import type { Slices, UniverseStackEntry } from './types.js';
import { getYjsHandle } from '../realtime/yjsAdapter.js';
import { api } from '../lib/api.js';

const HISTORY_LIMIT = 60;

// REST persistence helpers. Track in-flight node creates so links can await
// their endpoints before POST — prevents FK violations on drag-create-link flow.
const pendingNodePersist = new Map<string, Promise<unknown>>();

function persistNode(wsId: string, node: GraphNode) {
  const t0 = performance.now();
  const ts = new Date().toISOString().slice(11, 23);
  console.log(`[persist] node START ${ts} id=${node.id}`);
  const p = api<unknown>(`/workspaces/${wsId}/documents`, {
    method: 'POST',
    body: {
      id: node.id,
      title: node.label,
      color: node.color,
      category: node.category,
      type: node.type,
      parentId: node.parentId ?? null,
      icon: node.icon ?? null,
      isChildPage: node.parentId != null,
      content: node.content ?? null,
      position: { x: node.x, y: node.y, size: node.size, locked: node.locked ?? false },
    },
  })
    .then((r) => {
      const dt = (performance.now() - t0).toFixed(0);
      console.log(`[persist] node OK   ${new Date().toISOString().slice(11, 23)} id=${node.id} took=${dt}ms`);
      return r;
    })
    .catch((err) => {
      const dt = (performance.now() - t0).toFixed(0);
      console.error(`[persist] node FAIL ${new Date().toISOString().slice(11, 23)} id=${node.id} took=${dt}ms`, err);
      throw err;
    });
  pendingNodePersist.set(node.id, p);
  void p.finally(() => {
    if (pendingNodePersist.get(node.id) === p) pendingNodePersist.delete(node.id);
  });
}

function deleteNodeRest(id: string) {
  void api<unknown>(`/documents/${id}`, { method: 'DELETE' }).catch(() => {});
}

function persistLink(wsId: string, link: GraphLink) {
  const t0 = performance.now();
  console.log(`[persist] link START ${new Date().toISOString().slice(11, 23)} id=${link.id}`);
  const src = pendingNodePersist.get(link.source);
  const tgt = pendingNodePersist.get(link.target);
  const wait = src || tgt ? Promise.allSettled([src, tgt].filter(Boolean) as Promise<unknown>[]) : Promise.resolve();
  void wait.then(() =>
    api<unknown>(`/workspaces/${wsId}/links`, {
      method: 'POST',
      body: { id: link.id, sourceId: link.source, targetId: link.target, type: link.type, strength: link.strength },
    })
      .then((r) => {
        const dt = (performance.now() - t0).toFixed(0);
        console.log(`[persist] link OK   ${new Date().toISOString().slice(11, 23)} id=${link.id} took=${dt}ms`);
        return r;
      })
      .catch((err) => {
        const dt = (performance.now() - t0).toFixed(0);
        console.error(`[persist] link FAIL ${new Date().toISOString().slice(11, 23)} id=${link.id} took=${dt}ms`, err);
      }),
  );
}

function deleteLinkRest(id: string) {
  void api<unknown>(`/links/${id}`, { method: 'DELETE' }).catch(() => {});
}

function cleanForSnapshot(node: GraphNode): GraphNode {
  // Remove campos voláteis (vx/vy/_img...) — não ficam no snapshot.
  const { vx: _vx, vy: _vy, ...rest } = node;
  void _vx;
  void _vy;
  return { ...rest, vx: 0, vy: 0 };
}

function deepClone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}

export const useStore = create<Slices>()((set, get) => ({
  // ===== State =====
  nodes: [],
  links: [],
  selectedId: null,
  multiSelected: new Set<string>(),
  hoveredNode: null,
  focusedParentId: null,
  universeStack: [],
  searchQuery: '',
  drag: null,
  linkingFrom: null,
  linkDragPos: null,
  cam: { x: 0, y: 0, zoom: 1.0 },
  time: 0,
  physicsEnabled: true,
  snapToGrid: false,
  editingLabelId: null,
  contextMenu: null,
  cmdPaletteOpen: false,

  history: [],
  historyIdx: -1,

  userId: null,
  email: null,
  name: null,
  isAdmin: false,
  workspaceId: null,
  viewMode: 'graph',
  theme: 'dark',
  syncStatus: 'idle',
  remoteCursors: [],
  booted: false,
  chatOpen: false,
  assistantOpen: false,
  editPanelOpen: false,
  noteEditingId: null,
  noteCardBounds: null,
  docTabs: [],
  focusAudioOpen: false,

  // ===== Setters =====
  setNodes: (nodes) => set({ nodes }),
  setLinks: (links) => set({ links }),
  setSelected: (id) => set({ selectedId: id }),
  setMultiSelected: (s) => set({ multiSelected: s }),
  setHovered: (id) => set({ hoveredNode: id }),
  setCamera: (cam) => set({ cam }),
  setSearch: (q) => set({ searchQuery: q }),
  setLinkingFrom: (id) => set({ linkingFrom: id }),
  setLinkDragPos: (p) => set({ linkDragPos: p }),
  setDrag: (d) => set({ drag: d }),
  setViewMode: (m) => set({ viewMode: m, selectedId: null }),
  setTheme: (t) => {
    document.documentElement.setAttribute('data-theme', t === 'dark' ? '' : t);
    set({ theme: t });
  },
  setSyncStatus: (s) => set({ syncStatus: s }),
  setRemoteCursors: (c) => set({ remoteCursors: c }),
  setAuth: (u) => set({ userId: u?.id || null, email: u?.email || null, name: u?.name || null, isAdmin: u?.isAdmin || false }),
  setWorkspaceId: (id) => set({ workspaceId: id }),
  setChatOpen: (v) => set({ chatOpen: v }),
  setAssistantOpen: (v) => set({ assistantOpen: v }),
  setEditPanelOpen: (v) => set({ editPanelOpen: v }),
  setNoteEditingId: (id) => set({ noteEditingId: id, noteCardBounds: id ? get().noteCardBounds : null }),
  setNoteCardBounds: (b) => set({ noteCardBounds: b }),
  openDocTab: (id) => set((s) => (s.docTabs.includes(id) ? {} : { docTabs: [...s.docTabs, id] })),
  closeDocTab: (id) => set((s) => {
    const remaining = s.docTabs.filter((t) => t !== id);
    // If we closed the active tab, fall back to the previous tab (or null).
    let nextSelected = s.selectedId;
    if (s.selectedId === id) {
      const idx = s.docTabs.indexOf(id);
      nextSelected = remaining[idx - 1] ?? remaining[0] ?? null;
    }
    return { docTabs: remaining, selectedId: nextSelected };
  }),
  setFocusAudioOpen: (v) => set({ focusAudioOpen: v }),
  togglePhysics: (on) => set((s) => ({ physicsEnabled: on !== undefined ? on : !s.physicsEnabled })),
  setEditingLabelId: (id) => set({ editingLabelId: id }),
  setContextMenu: (m) => set({ contextMenu: m }),
  setCmdPaletteOpen: (v) => set({ cmdPaletteOpen: v }),
  toggleSnapToGrid: () => set((s) => {
    const next = !s.snapToGrid;
    // Snap-to-grid implies fixed positions — auto-freeze physics so nodes don't drift off grid.
    return { snapToGrid: next, physicsEnabled: next ? false : s.physicsEnabled };
  }),

  autoLayout: () => {
    const { nodes, links } = get();
    if (nodes.length < 2) return;

    const K_REPEL = 9000;
    const K_SPRING = 0.07;
    const REST_LEN = 200;
    const CENTER_G = 0.012;
    const STEPS = 250;
    const GRID_SIZE = 40;

    type P = { x: number; y: number; fx: number; fy: number };
    const pos = new Map<string, P>(nodes.map((n) => [n.id, { x: n.x, y: n.y, fx: 0, fy: 0 }]));
    const arr = [...pos.entries()];

    for (let step = 0; step < STEPS; step++) {
      const decay = 1 - step / STEPS;
      for (const [, p] of arr) { p.fx = 0; p.fy = 0; }

      // Repulsion O(n²)
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const [, a] = arr[i]!;
          const [, b] = arr[j]!;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d2 = dx * dx + dy * dy + 64;
          const d = Math.sqrt(d2);
          const f = K_REPEL / d2;
          a.fx -= (dx / d) * f; a.fy -= (dy / d) * f;
          b.fx += (dx / d) * f; b.fy += (dy / d) * f;
        }
      }

      // Spring
      for (const lk of links) {
        const a = pos.get(lk.source);
        const b = pos.get(lk.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const d = Math.hypot(dx, dy) || 1;
        const f = (d - REST_LEN) * K_SPRING;
        a.fx += (dx / d) * f; a.fy += (dy / d) * f;
        b.fx -= (dx / d) * f; b.fy -= (dy / d) * f;
      }

      // Center gravity
      for (const [, p] of arr) {
        p.fx -= p.x * CENTER_G;
        p.fy -= p.y * CENTER_G;
      }

      // Integrate
      const dt = 0.6 * decay;
      for (const [, p] of arr) {
        p.x += p.fx * dt;
        p.y += p.fy * dt;
      }
    }

    get().snapshot();
    set((s) => ({
      nodes: s.nodes.map((n) => {
        if (n.locked) return n;
        const p = pos.get(n.id);
        if (!p) return n;
        const sx = Math.round(p.x / GRID_SIZE) * GRID_SIZE;
        const sy = Math.round(p.y / GRID_SIZE) * GRID_SIZE;
        return { ...n, x: sx, y: sy, vx: 0, vy: 0 };
      }),
    }));
  },
  setFocusedParentId: (id) => set({ focusedParentId: id }),

  // ===== Tick (chama engine) =====
  tick: (dt) => {
    const state = get();
    tickPhysics({
      nodes: state.nodes,
      links: state.links,
      drag: state.drag,
      time: state.time,
      searchQuery: state.searchQuery,
      focusedParentId: state.focusedParentId,
      physicsEnabled: state.physicsEnabled,
    });
    set({ time: state.time + dt });
  },

  // ===== History =====
  snapshot: () => {
    const { booted, nodes, links, focusedParentId, history, historyIdx } = get();
    if (!booted) return;
    const s = JSON.stringify({
      nodes: nodes.map(cleanForSnapshot),
      links,
      focusedParentId,
    });
    if (history[historyIdx] === s) return;
    const newHistory = history.slice(0, historyIdx + 1);
    newHistory.push(s);
    if (newHistory.length > HISTORY_LIMIT) newHistory.shift();
    set({ history: newHistory, historyIdx: newHistory.length - 1 });
  },

  undo: () => {
    const { historyIdx, history } = get();
    if (historyIdx <= 0) return;
    const prev = historyIdx - 1;
    const snap = history[prev];
    if (!snap) return;
    get().applySnapshot(JSON.parse(snap));
    set({ historyIdx: prev });
  },

  redo: () => {
    const { historyIdx, history } = get();
    if (historyIdx >= history.length - 1) return;
    const next = historyIdx + 1;
    const snap = history[next];
    if (!snap) return;
    get().applySnapshot(JSON.parse(snap));
    set({ historyIdx: next });
  },

  jumpToHistory: (idx) => {
    const { history } = get();
    const snap = history[idx];
    if (!snap) return;
    get().applySnapshot(JSON.parse(snap));
    set({ historyIdx: idx });
  },

  applySnapshot: (data) => {
    const nodes = data.nodes.map((n) => ({ ...n, vx: 0, vy: 0 }));
    set({
      nodes,
      links: data.links,
      focusedParentId: data.focusedParentId,
      selectedId: null,
      multiSelected: new Set(),
    });
  },

  // ===== Node CRUD =====
  addNode: (input) => {
    get().snapshot();
    const t = (input.type ?? 'idea') as keyof typeof NODE_DEFAULTS;
    const defs = NODE_DEFAULTS[t] ?? NODE_DEFAULTS.idea!;
    const id = input.id ?? uid();
    const parentId = input.parentId ?? get().focusedParentId;

    // Auto-increment label if duplicate exists at same level.
    let baseLabel = input.label ?? 'Nova Ideia';
    const siblings = get().nodes.filter((n) => (n.parentId ?? null) === (parentId ?? null));
    if (siblings.some((n) => n.label === baseLabel)) {
      let i = 2;
      while (siblings.some((n) => n.label === `${baseLabel} ${i}`)) i++;
      baseLabel = `${baseLabel} ${i}`;
    }

    const newNode: GraphNode = {
      id,
      x: input.x ?? 0,
      y: input.y ?? 0,
      vx: 0,
      vy: 0,
      size: input.size ?? defs.defaultSize,
      locked: input.locked ?? false,
      parentId,
      label: baseLabel,
      type: (input.type ?? 'idea') as GraphNode['type'],
      category: (input.category ?? 'idea') as GraphNode['category'],
      color: input.color ?? CATEGORY_MAP[(input.category ?? 'idea') as keyof typeof CATEGORY_MAP]?.color ?? '#00f5ff',
      icon: input.icon ?? defs.defaultIcon,
      content: input.content ?? '',
    };
    set((s) => ({ nodes: [...s.nodes, newNode] }));
    console.log(`[ui] addNode VISUAL ${new Date().toISOString().slice(11, 23)} id=${id}`);
    getYjsHandle()?.upsertNode({
      id: newNode.id, x: newNode.x, y: newNode.y, size: newNode.size,
      locked: newNode.locked, label: newNode.label, color: newNode.color,
      category: newNode.category, type: newNode.type,
      parentId: newNode.parentId ?? null, icon: newNode.icon, content: newNode.content,
    });
    const wsId = get().workspaceId;
    if (wsId) persistNode(wsId, newNode);
    return id;
  },

  deleteNode: (id) => {
    get().snapshot();
    set((s) => {
      const multi = new Set(s.multiSelected);
      multi.delete(id);
      return {
        nodes: s.nodes.filter((n) => n.id !== id),
        links: s.links.filter((l) => l.source !== id && l.target !== id),
        selectedId: s.selectedId === id ? null : s.selectedId,
        multiSelected: multi,
      };
    });
    getYjsHandle()?.removeNode(id);
    deleteNodeRest(id);
  },

  patchNode: (id, patch) => {
    set((s) => ({
      nodes: s.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
  },

  updateNode: (id, patch) => {
    get().snapshot();
    set((s) => ({
      nodes: s.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
    }));
    const updated = get().nodes.find((n) => n.id === id);
    if (updated) {
      getYjsHandle()?.upsertNode({
        id, x: updated.x, y: updated.y, size: updated.size,
        locked: updated.locked, label: updated.label, color: updated.color,
        category: updated.category, type: updated.type,
        parentId: updated.parentId ?? null, icon: updated.icon, content: updated.content,
      });
      const wsId = get().workspaceId;
      if (wsId) {
        void api<unknown>(`/documents/${id}`, {
          method: 'PATCH',
          body: {
            title: updated.label, color: updated.color, category: updated.category,
            type: updated.type, parentId: updated.parentId ?? null, icon: updated.icon ?? null,
            isChildPage: updated.parentId != null,
            content: updated.content ?? null,
            position: { x: updated.x, y: updated.y, size: updated.size, locked: updated.locked ?? false },
          },
        }).catch((err) => console.error('[persist] updateNode failed', id, err));
      }
    }
  },

  duplicateNode: (id) => {
    get().snapshot();
    const src = get().nodes.find((n) => n.id === id);
    if (!src) return;
    const newId = uid();
    const dup: GraphNode = {
      ...src,
      id: newId,
      label: src.label + ' (cópia)',
      x: src.x + 40,
      y: src.y + 40,
      vx: 0,
      vy: 0,
    };
    const newLinks = get().links
      .filter((l) => l.source === id || l.target === id)
      .map((l) => ({
        ...l,
        id: uid(),
        source: l.source === id ? newId : l.source,
        target: l.target === id ? newId : l.target,
      }));
    set((s) => ({ nodes: [...s.nodes, dup], links: [...s.links, ...newLinks] }));
    const handle = getYjsHandle();
    handle?.upsertNode({
      id: dup.id, x: dup.x, y: dup.y, size: dup.size,
      locked: dup.locked, label: dup.label, color: dup.color,
      category: dup.category, type: dup.type,
      parentId: dup.parentId ?? null, icon: dup.icon, content: dup.content,
    });
    const wsId = get().workspaceId;
    if (wsId) {
      persistNode(wsId, dup);
      for (const lk of newLinks) {
        handle?.upsertLink({ id: lk.id, source: lk.source, target: lk.target, type: lk.type, strength: lk.strength });
        persistLink(wsId, lk);
      }
    }
  },

  toggleNodeLock: (id) => {
    get().snapshot();
    set((s) => ({
      nodes: s.nodes.map((n) => (n.id === id ? { ...n, locked: !n.locked } : n)),
    }));
  },

  createLink: (source, target, type = 'synapse') => {
    if (source === target) return;
    const { links } = get();
    const exists = links.some(
      (l) =>
        (l.source === source && l.target === target) ||
        (l.source === target && l.target === source),
    );
    if (exists) return;
    get().snapshot();
    const link: GraphLink = { id: uid(), source, target, type, strength: 0.5 };
    set((s) => ({ links: [...s.links, link] }));
    getYjsHandle()?.upsertLink({ id: link.id, source, target, type, strength: 0.5 });
    const wsId = get().workspaceId;
    if (wsId) persistLink(wsId, link);
  },

  removeLink: (sourceId, targetId) => {
    get().snapshot();
    const linkToRemove = get().links.find(
      (l) =>
        (l.source === sourceId && l.target === targetId) ||
        (l.source === targetId && l.target === sourceId),
    );
    set((s) => ({
      links: s.links.filter(
        (l) =>
          !((l.source === sourceId && l.target === targetId) ||
            (l.source === targetId && l.target === sourceId)),
      ),
    }));
    if (linkToRemove) {
      getYjsHandle()?.removeLink(linkToRemove.id);
      deleteLinkRest(linkToRemove.id);
    }
  },

  // ===== Universe navigation =====
  // All nodes stay in state.nodes at ALL times. Navigation only changes focusedParentId.
  // universeStack is breadcrumb-only (no snapshot of nodes/links).

  enterUniverse: (nodeId) => {
    const { nodes, focusedParentId, cam, universeStack } = get();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;
    get().snapshot();
    set({
      universeStack: [...universeStack, { nodeId, label: node.label, prevFocusedParentId: focusedParentId, prevCam: { ...cam } }],
      focusedParentId: nodeId,
      cam: { x: 0, y: 0, zoom: 1 },
      selectedId: null,
      multiSelected: new Set(),
    });
  },

  exitUniverse: () => {
    const { universeStack } = get();
    if (!universeStack.length) return;
    get().snapshot();
    const stack = [...universeStack];
    const entry = stack.pop()!;
    set({
      universeStack: stack,
      focusedParentId: entry.prevFocusedParentId,
      cam: entry.prevCam,
      selectedId: null,
      multiSelected: new Set(),
    });
  },

  zoomToFit: () => {
    const { nodes } = get();
    if (nodes.length === 0) return;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of nodes) {
      const r = n.size || 22;
      minX = Math.min(minX, n.x - r);
      maxX = Math.max(maxX, n.x + r);
      minY = Math.min(minY, n.y - r);
      maxY = Math.max(maxY, n.y + r);
    }
    const pad = 80;
    const bboxW = maxX - minX + pad * 2;
    const bboxH = maxY - minY + pad * 2;
    const topbarH = 54;
    const canvasW = window.innerWidth;
    const canvasH = window.innerHeight - topbarH;
    const newZoom = Math.min(6, Math.max(0.06, Math.min(canvasW / bboxW, canvasH / bboxH)));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    set({ cam: { x: -cx, y: -cy, zoom: newZoom } });
  },

  jumpToUniverse: (stackIdx) => {
    const { universeStack } = get();
    if (!universeStack.length) return;
    get().snapshot();
    if (stackIdx === -1) {
      // Jump to root (Córtex).
      set({
        focusedParentId: null,
        cam: universeStack[0]!.prevCam,
        universeStack: [],
        selectedId: null,
        multiSelected: new Set(),
      });
      return;
    }
    if (stackIdx >= 0 && stackIdx < universeStack.length - 1) {
      // Jump to intermediate level: show children of that entry's node.
      const entry = universeStack[stackIdx]!;
      const prevCamAtLevel = universeStack[stackIdx + 1]!.prevCam;
      set({
        focusedParentId: entry.nodeId,
        cam: prevCamAtLevel,
        universeStack: universeStack.slice(0, stackIdx + 1),
        selectedId: null,
        multiSelected: new Set(),
      });
    }
  },
}));

// Hook de E2E/debug: dá acesso ao store via console/automação de browser.
// Cliente-side apenas — usuário já controla o próprio cliente.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__synapseStore = useStore;
}

export function selectVisibleNodes(state: Slices) {
  return getVisibleNodes({
    nodes: state.nodes,
    links: state.links,
    drag: state.drag,
    time: state.time,
    searchQuery: state.searchQuery,
    focusedParentId: state.focusedParentId,
    physicsEnabled: state.physicsEnabled,
  });
}
